// =====================================================
// DrawingCanvas.tsx - Componente de Dibujo (mini Paint)
// Para campo "Árbol Genealógico" en Historias Clínicas
// Guarda como base64 PNG en form_data
// =====================================================

import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Pen,
  Eraser,
  Undo2,
  Redo2,
  Trash2,
  Download,
  Circle,
  Square,
  Minus,
  Type,
  Palette,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';

interface DrawingCanvasProps {
  value?: string;            // base64 del dibujo guardado
  onChange: (base64: string) => void;
  label?: string;
  width?: number;
  height?: number;
  readOnly?: boolean;
}

type Tool = 'pen' | 'eraser' | 'line' | 'rect' | 'circle' | 'text';

interface HistoryEntry {
  imageData: ImageData;
}

const COLORS = [
  '#000000', '#FF0000', '#0000FF', '#008000', 
  '#FF6600', '#800080', '#8B4513', '#666666'
];

const SIZES = [2, 4, 6, 10];

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  value,
  onChange,
  label = 'Área de dibujo',
  width = 800,
  height = 500,
  readOnly = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(2);
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // History para undo/redo
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Para formas (línea, rectángulo, círculo)
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const snapshotRef = useRef<ImageData | null>(null);
  
  // Para texto
  const [textInput, setTextInput] = useState('');
  const [textPos, setTextPos] = useState<{ x: number; y: number } | null>(null);
  
  // Canvas responsivo
  const [canvasScale, setCanvasScale] = useState(1);
  
  // Fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenCanvasRef = useRef<HTMLCanvasElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenScale, setFullscreenScale] = useState(1);
  
  // Inicializar canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Fondo blanco
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    
    // Si hay un valor guardado, cargar la imagen
    if (value && value.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        saveToHistory();
      };
      img.src = value;
    } else {
      saveToHistory();
    }
  }, []);
  
  // Ajustar escala del canvas al contenedor
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const scale = Math.min(1, (containerWidth - 2) / width);
        setCanvasScale(scale);
      }
    };
    
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [width]);
  
  // Guardar estado al historial
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const imageData = ctx.getImageData(0, 0, width, height);
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push({ imageData });
      // Limitar historial a 30 entradas
      if (newHistory.length > 30) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 29));
  }, [historyIndex, width, height]);
  
  // Emitir cambio al padre
  const emitChange = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const base64 = canvas.toDataURL('image/png');
    onChange(base64);
  }, [onChange]);
  
  // Obtener posición del mouse/touch relativa al canvas
  const getPosition = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) / canvasScale,
      y: (clientY - rect.top) / canvasScale
    };
  };
  
  // --- Eventos de dibujo ---
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (readOnly) return;
    e.preventDefault();
    
    const pos = getPosition(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    
    if (tool === 'text') {
      setTextPos(pos);
      return;
    }
    
    setIsDrawing(true);
    startPos.current = pos;
    
    // Guardar snapshot para formas
    if (tool === 'line' || tool === 'rect' || tool === 'circle') {
      snapshotRef.current = ctx.getImageData(0, 0, width, height);
    }
    
    if (tool === 'pen' || tool === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = tool === 'eraser' ? '#FFFFFF' : color;
      ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };
  
  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault();
    
    const pos = getPosition(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !startPos.current) return;
    
    if (tool === 'pen' || tool === 'eraser') {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (snapshotRef.current) {
      // Restaurar snapshot y dibujar la forma previa
      ctx.putImageData(snapshotRef.current, 0, 0);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      
      const sx = startPos.current.x;
      const sy = startPos.current.y;
      
      if (tool === 'line') {
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      } else if (tool === 'rect') {
        ctx.beginPath();
        ctx.strokeRect(sx, sy, pos.x - sx, pos.y - sy);
      } else if (tool === 'circle') {
        const rx = Math.abs(pos.x - sx) / 2;
        const ry = Math.abs(pos.y - sy) / 2;
        const cx = sx + (pos.x - sx) / 2;
        const cy = sy + (pos.y - sy) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  };
  
  const handleEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || readOnly) return;
    setIsDrawing(false);
    startPos.current = null;
    snapshotRef.current = null;
    saveToHistory();
    emitChange();
  };
  
  // Insertar texto
  const handleTextSubmit = () => {
    if (!textPos || !textInput.trim()) {
      setTextPos(null);
      setTextInput('');
      return;
    }
    
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    ctx.font = `${lineWidth * 5 + 10}px Arial`;
    ctx.fillStyle = color;
    ctx.fillText(textInput, textPos.x, textPos.y);
    
    setTextPos(null);
    setTextInput('');
    saveToHistory();
    emitChange();
  };
  
  // Undo
  const handleUndo = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !history[newIndex]) return;
    
    ctx.putImageData(history[newIndex].imageData, 0, 0);
    setHistoryIndex(newIndex);
    emitChange();
  };
  
  // Redo
  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !history[newIndex]) return;
    
    ctx.putImageData(history[newIndex].imageData, 0, 0);
    setHistoryIndex(newIndex);
    emitChange();
  };
  
  // Limpiar canvas
  const handleClear = () => {
    if (!window.confirm('¿Borrar todo el dibujo?')) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    saveToHistory();
    emitChange();
  };
  
  // Descargar imagen
  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `arbol-genealogico-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  
  // Abrir fullscreen: copiar canvas principal al fullscreen
  const openFullscreen = () => {
    setIsFullscreen(true);
    // Copiar contenido después de que el modal se renderice
    setTimeout(() => {
      const srcCanvas = canvasRef.current;
      const dstCanvas = fullscreenCanvasRef.current;
      if (!srcCanvas || !dstCanvas) return;
      
      const dstCtx = dstCanvas.getContext('2d');
      if (!dstCtx) return;
      
      dstCtx.fillStyle = '#FFFFFF';
      dstCtx.fillRect(0, 0, width, height);
      dstCtx.drawImage(srcCanvas, 0, 0);
      
      // Calcular escala fullscreen
      if (fullscreenContainerRef.current) {
        const cw = fullscreenContainerRef.current.clientWidth - 4;
        const ch = fullscreenContainerRef.current.clientHeight - 4;
        const scale = Math.min(cw / width, ch / height, 1);
        setFullscreenScale(scale);
      }
    }, 50);
  };
  
  // Cerrar fullscreen: copiar canvas fullscreen al principal
  const closeFullscreen = () => {
    const srcCanvas = fullscreenCanvasRef.current;
    const dstCanvas = canvasRef.current;
    if (srcCanvas && dstCanvas) {
      const dstCtx = dstCanvas.getContext('2d');
      if (dstCtx) {
        dstCtx.fillStyle = '#FFFFFF';
        dstCtx.fillRect(0, 0, width, height);
        dstCtx.drawImage(srcCanvas, 0, 0);
        saveToHistory();
        emitChange();
      }
    }
    setIsFullscreen(false);
  };
  
  // Eventos de dibujo en fullscreen
  const getFullscreenPosition = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = fullscreenCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) / fullscreenScale,
      y: (clientY - rect.top) / fullscreenScale
    };
  };
  
  const handleFullscreenStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getFullscreenPosition(e);
    const canvas = fullscreenCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    
    if (tool === 'text') {
      setTextPos(pos);
      return;
    }
    
    setIsDrawing(true);
    startPos.current = pos;
    
    if (tool === 'line' || tool === 'rect' || tool === 'circle') {
      snapshotRef.current = ctx.getImageData(0, 0, width, height);
    }
    
    if (tool === 'pen' || tool === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = tool === 'eraser' ? '#FFFFFF' : color;
      ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };
  
  const handleFullscreenMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const pos = getFullscreenPosition(e);
    const canvas = fullscreenCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !startPos.current) return;
    
    if (tool === 'pen' || tool === 'eraser') {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (snapshotRef.current) {
      ctx.putImageData(snapshotRef.current, 0, 0);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      
      const sx = startPos.current.x;
      const sy = startPos.current.y;
      
      if (tool === 'line') {
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      } else if (tool === 'rect') {
        ctx.beginPath();
        ctx.strokeRect(sx, sy, pos.x - sx, pos.y - sy);
      } else if (tool === 'circle') {
        const rx = Math.abs(pos.x - sx) / 2;
        const ry = Math.abs(pos.y - sy) / 2;
        const cx = sx + (pos.x - sx) / 2;
        const cy = sy + (pos.y - sy) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  };
  
  const handleFullscreenEnd = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    startPos.current = null;
    snapshotRef.current = null;
  };
  
  const handleFullscreenTextSubmit = () => {
    if (!textPos || !textInput.trim()) {
      setTextPos(null);
      setTextInput('');
      return;
    }
    
    const ctx = fullscreenCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    ctx.font = `${lineWidth * 5 + 10}px Arial`;
    ctx.fillStyle = color;
    ctx.fillText(textInput, textPos.x, textPos.y);
    
    setTextPos(null);
    setTextInput('');
  };
  
  // Vista de solo lectura
  if (readOnly && value) {
    return (
      <div className="space-y-2">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <img 
            src={value} 
            alt={label}
            className="w-full h-auto"
            style={{ maxHeight: '500px', objectFit: 'contain' }}
          />
        </div>
        <button
          type="button"
          onClick={handleDownload}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
        >
          <Download className="w-4 h-4" /> Descargar imagen
        </button>
      </div>
    );
  }
  
  if (readOnly) return null;
  
  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: 'pen', icon: <Pen className="w-4 h-4" />, label: 'Lápiz' },
    { id: 'eraser', icon: <Eraser className="w-4 h-4" />, label: 'Borrador' },
    { id: 'line', icon: <Minus className="w-4 h-4" />, label: 'Línea' },
    { id: 'rect', icon: <Square className="w-4 h-4" />, label: 'Rectángulo' },
    { id: 'circle', icon: <Circle className="w-4 h-4" />, label: 'Círculo' },
    { id: 'text', icon: <Type className="w-4 h-4" />, label: 'Texto' },
  ];
  
  return (
    <div className="space-y-2">
      {/* Barra de herramientas */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 rounded-xl border border-gray-200">
        
        {/* Herramientas de dibujo */}
        <div className="flex items-center gap-0.5 border-r border-gray-300 pr-2 mr-1">
          {tools.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTool(t.id)}
              title={t.label}
              className={`p-2 rounded-lg transition-all ${
                tool === t.id
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.icon}
            </button>
          ))}
        </div>
        
        {/* Grosor */}
        <div className="flex items-center gap-0.5 border-r border-gray-300 pr-2 mr-1">
          {SIZES.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setLineWidth(s)}
              title={`Grosor ${s}px`}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                lineWidth === s
                  ? 'bg-gray-800 shadow-sm'
                  : 'hover:bg-gray-200'
              }`}
            >
              <div 
                className="rounded-full"
                style={{ 
                  width: Math.min(s + 2, 14), 
                  height: Math.min(s + 2, 14), 
                  backgroundColor: lineWidth === s ? 'white' : '#374151'
                }} 
              />
            </button>
          ))}
        </div>
        
        {/* Colores */}
        <div className="flex items-center gap-0.5 border-r border-gray-300 pr-2 mr-1">
          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              title={c}
              className={`w-7 h-7 rounded-full border-2 transition-all ${
                color === c ? 'border-blue-500 scale-110 shadow-sm' : 'border-gray-300 hover:scale-105'
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        
        {/* Acciones */}
        <div className="flex items-center gap-0.5 ml-auto">
          <button
            type="button"
            onClick={openFullscreen}
            title="Pantalla completa"
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            title="Deshacer"
            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            title="Rehacer"
            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            title="Descargar"
            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-all"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleClear}
            title="Borrar todo"
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Canvas */}
      <div 
        ref={containerRef}
        className="relative border-2 border-gray-300 rounded-xl overflow-hidden bg-white cursor-crosshair"
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          style={{
            width: `${width * canvasScale}px`,
            height: `${height * canvasScale}px`,
            display: 'block'
          }}
        />
        
        {/* Input de texto flotante */}
        {textPos && tool === 'text' && (
          <div 
            className="absolute z-10"
            style={{ 
              left: textPos.x * canvasScale, 
              top: textPos.y * canvasScale - 15 
            }}
          >
            <div className="flex items-center gap-1 bg-white shadow-lg rounded-lg border border-blue-400 p-1">
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTextSubmit();
                  if (e.key === 'Escape') { setTextPos(null); setTextInput(''); }
                }}
                autoFocus
                placeholder="Escribí el texto..."
                className="px-2 py-1 text-sm border-none outline-none w-40"
                style={{ color }}
              />
              <button
                type="button"
                onClick={handleTextSubmit}
                className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Indicador de herramienta activa */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>
          🖊 {tools.find(t => t.id === tool)?.label} | Grosor: {lineWidth}px | 
          <span className="inline-block w-3 h-3 rounded-full ml-1 align-middle border border-gray-300" style={{ backgroundColor: color }} />
        </span>
        <button
          type="button"
          onClick={openFullscreen}
          className="text-blue-500 hover:text-blue-700 flex items-center gap-1 transition-colors"
        >
          <Maximize2 className="w-3 h-3" /> Ampliar
        </button>
      </div>
      
      {/* Modal Fullscreen */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex flex-col">
          {/* Header del fullscreen - responsive */}
          <div className="bg-gray-900 text-white px-2 py-1.5 space-y-1">
            {/* Fila 1: Título + Guardar */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">{label}</span>
              <button
                type="button"
                onClick={closeFullscreen}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-1 text-sm font-medium flex-shrink-0"
              >
                <Minimize2 className="w-4 h-4" /> Guardar
              </button>
            </div>
            
            {/* Fila 2: Herramientas + Grosores */}
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-0.5">
                {tools.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTool(t.id)}
                    title={t.label}
                    className={`p-1.5 rounded transition-all ${
                      tool === t.id
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {t.icon}
                  </button>
                ))}
              </div>
              
              <div className="w-px h-5 bg-gray-600 mx-1" />
              
              <div className="flex items-center gap-0.5">
                {SIZES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setLineWidth(s)}
                    className={`w-7 h-7 rounded flex items-center justify-center transition-all ${
                      lineWidth === s ? 'bg-gray-600' : 'hover:bg-gray-700'
                    }`}
                  >
                    <div 
                      className="rounded-full bg-white"
                      style={{ width: Math.min(s + 2, 14), height: Math.min(s + 2, 14) }} 
                    />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Fila 3: Colores */}
            <div className="flex items-center gap-1">
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all flex-shrink-0 ${
                    color === c ? 'border-white scale-110' : 'border-gray-500 hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          
          {/* Canvas area */}
          <div 
            ref={fullscreenContainerRef}
            className="flex-1 flex items-center justify-center p-2 overflow-hidden"
            style={{ touchAction: 'none' }}
          >
            <canvas
              ref={fullscreenCanvasRef}
              width={width}
              height={height}
              onMouseDown={handleFullscreenStart}
              onMouseMove={handleFullscreenMove}
              onMouseUp={handleFullscreenEnd}
              onMouseLeave={handleFullscreenEnd}
              onTouchStart={handleFullscreenStart}
              onTouchMove={handleFullscreenMove}
              onTouchEnd={handleFullscreenEnd}
              className="bg-white rounded-lg shadow-2xl cursor-crosshair"
              style={{
                width: `${width * fullscreenScale}px`,
                height: `${height * fullscreenScale}px`,
              }}
            />
            
            {/* Input de texto en fullscreen */}
            {textPos && tool === 'text' && (
              <div 
                className="absolute z-10"
                style={{ 
                  left: `calc(50% - ${(width * fullscreenScale) / 2}px + ${textPos.x * fullscreenScale}px)`,
                  top: `calc(50% - ${(height * fullscreenScale) / 2}px + ${textPos.y * fullscreenScale}px)`
                }}
              >
                <div className="flex items-center gap-1 bg-white shadow-lg rounded-lg border border-blue-400 p-1">
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFullscreenTextSubmit();
                      if (e.key === 'Escape') { setTextPos(null); setTextInput(''); }
                    }}
                    autoFocus
                    placeholder="Escribí el texto..."
                    className="px-2 py-1 text-sm border-none outline-none w-40"
                    style={{ color }}
                  />
                  <button
                    type="button"
                    onClick={handleFullscreenTextSubmit}
                    className="px-2 py-1 bg-blue-500 text-white text-xs rounded"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingCanvas;